import type { WorkflowShareRole } from '@/types/share-types';
import type { WorkflowDetails } from '@/types/workflow-types';

export interface WorkflowCommandOption {
  workflowId: string;
  name: string;
  description?: string;
  lastRunAt?: string | null;
}

export const WORKFLOW_MENU_LIMIT = 6;

const RUNNABLE_WORKFLOW_ROLES: ReadonlySet<WorkflowShareRole> = new Set([
  'workflow:owner',
  'workflow:editor',
]);

function canRunWorkflow(workflow: WorkflowDetails): boolean {
  return RUNNABLE_WORKFLOW_ROLES.has(workflow.user_role as WorkflowShareRole);
}

export function toWorkflowOptions(
  workflows: WorkflowDetails[],
  options: { isWorkflowAdmin?: boolean } = {},
): WorkflowCommandOption[] {
  const runnable = options.isWorkflowAdmin
    ? workflows
    : workflows.filter(canRunWorkflow);
  return runnable.map((w) => ({
    workflowId: w.workflow_id,
    name: w.name,
    description: w.description || undefined,
    lastRunAt: w.last_run_at ?? null,
  }));
}

function byRecency(a: WorkflowCommandOption, b: WorkflowCommandOption): number {
  if (a.lastRunAt && b.lastRunAt) {
    return b.lastRunAt.localeCompare(a.lastRunAt);
  }
  if (a.lastRunAt) return -1;
  if (b.lastRunAt) return 1;
  return a.name.localeCompare(b.name);
}

export function filterWorkflowOptions(
  options: WorkflowCommandOption[],
  query: string,
  limit: number = WORKFLOW_MENU_LIMIT,
): WorkflowCommandOption[] {
  const q = query.trim().toLowerCase();
  const matches = q
    ? options.filter((o) => o.name.toLowerCase().includes(q))
    : options;
  return [...matches].sort(byRecency).slice(0, limit);
}

export function workflowRunMessage(
  workflow: Pick<WorkflowCommandOption, 'workflowId' | 'name'>,
  brief: string,
): string {
  const base = `Run the workflow "${workflow.name}" (workflow_id: ${workflow.workflowId}).`;
  const trimmed = brief.trim();
  return trimmed ? `${base} Request: ${trimmed}` : base;
}
